import { createApi } from "@reduxjs/toolkit/query/react";
import { APP_BASE_URL, GET_KAVACH_EVENT_LOGS } from '../../../Common/EndPoints';
import axiosBaseQuery from "../../../features/api/rtk-query-axios.service";
import {
  EventLogsApiResponse,
  GetEventLogsParams,
  TransformedEventLogsResponse,
  EventLogItem,
} from '../utils/kavachApiTypes';
import { transformEventLogsToKavachData } from '../utils/kavachDataTransform';

// History response interface
export interface KavachHistoryResponse {
  content: EventLogItem[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
}

export const kavachEventLogsApi = createApi({
  reducerPath: 'kavachEventLogsApi',
  baseQuery: axiosBaseQuery,
  tagTypes: ['kavachEventLogs'],
  endpoints: (builder) => ({
    getEventLogs: builder.query<TransformedEventLogsResponse, GetEventLogsParams>({
      query: (params) => ({
        url: APP_BASE_URL + GET_KAVACH_EVENT_LOGS,
        method: "GET",
        params: {
          page: params.page ?? 0,
          size: params.size ?? 200,
          nameLike: 'KAVACH',
        },
      }),
      transformResponse: (response: EventLogsApiResponse): TransformedEventLogsResponse => {
        if (!response.success || !response.data) {
          return {
            switches: [],
            alerts: [],
            stats: {
              totalSwitches: 0,
              portsUp: 0,
              portsDown: 0,
              activeAlerts: 0,
              upTrend: 0,
              downTrend: 0,
            },
            stationStats: [],
            stations: ['ALL'],
            totalElements: 0,
            totalPages: 0,
            currentPage: 0,
          };
        }

        const { content, totalElements, totalPages, number } = response.data;
        return transformEventLogsToKavachData(content, totalElements, totalPages, number);
      },
      providesTags: ['kavachEventLogs'],
    }),

    // History endpoint with date range filtering
    getEventLogsHistory: builder.query<KavachHistoryResponse, GetEventLogsParams>({
      query: (params) => ({
        method: "GET",
        url: GET_KAVACH_EVENT_LOGS,
        params: {
          page: params.page ?? 0,
          size: params.size ?? 100,
          deviceId: params.deviceId,
          fromDate: params.fromDate,
          toDate: params.toDate,
          nameLike: 'KAVACH',
        },
      }),
      transformResponse: (response: EventLogsApiResponse): KavachHistoryResponse => {
        if (!response.success || !response.data) {
          return {
            content: [],
            totalElements: 0,
            totalPages: 0,
            currentPage: 0,
          };
        }

        const { content, totalElements, totalPages, number } = response.data;
        return {
          content,
          totalElements,
          totalPages,
          currentPage: number,
        };
      },
      providesTags: ['kavachEventLogs'],
    }),
  }),
});

export const { useGetEventLogsQuery, useGetEventLogsHistoryQuery } = kavachEventLogsApi;
